import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  CheckCircle, 
  AlertCircle,
  Users,
  HandshakeIcon,
  ShieldCheck,
  Loader2,
  ChevronDown,
  XCircle,
  ArrowRight,
  Target
} from 'lucide-react';

const PartnerProgramm = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('partner.form.errors.name');
    if (!formData.company.trim()) newErrors.company = t('partner.form.errors.company');
    if (!formData.email.trim()) {
      newErrors.email = t('partner.form.errors.email');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('partner.form.errors.emailInvalid');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL}/api/partner-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          pageSource: 'Partner Programm',
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', company: '', email: '', message: '' });
      } else {
        throw new Error('Network response was not ok');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: t('partner.form.errors.submit') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('partner-contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead 
        title={t('partner.seo.title')}
        description={t('partner.seo.description')}
        keywords={t('partner.seo.keywords')}
        canonicalUrl="/partnerprogramm"
      />
      <div className="theme-brand min-h-screen bg-surface-sunken text-ink">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className=" text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                {t('partner.hero.title')}
              </h1>
              <p className="text-xl md:text-2xl text-ink-muted max-w-4xl mx-auto leading-relaxed">
                {t('partner.hero.subtitle')}
              </p>
              <button 
                onClick={scrollToForm}
                className="mt-12 text-ink-faint hover:text-white transition-colors flex items-center gap-2 mx-auto"
              >
                <ChevronDown className="h-5 w-5 animate-bounce" />
              </button>
            </div>
          </div>
        </section>

        {/* Was ist das BROVKO-Partnerprogramm? */}
        <section className="py-20 md:py-28 bg-surface">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-6">
              {t('partner.intro.title')}
            </h2>
            <div className="space-y-4 text-ink-muted text-lg leading-relaxed">
              <p>{t('partner.intro.p1')}</p>
              <p>{t('partner.intro.p2')}</p>
              <p>{t('partner.intro.p3')}</p>
            </div>
          </div>
        </section>

        {/* Ausgangslage */}
        <section className="py-20 md:py-28 bg-surface-sunken">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-6">
              {t('partner.situation.title')}
            </h2>
            <p className="text-ink-muted mb-4 font-medium">
              {t('partner.situation.intro')}
            </p>
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-1" />
                <p className="text-ink-muted">{t('partner.situation.point1')}</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-1" />
                <p className="text-ink-muted">{t('partner.situation.point2')}</p>
              </div>
            </div>

            <p className="text-ink-muted mb-4 font-medium">
              {t('partner.situation.challenge')}
            </p>
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-1" />
                <p className="text-ink-muted">{t('partner.situation.challenge1')}</p>
              </div>
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-1" />
                <p className="text-ink-muted">{t('partner.situation.challenge2')}</p>
              </div>
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-1" />
                <p className="text-ink-muted">{t('partner.situation.challenge3')}</p>
              </div>
            </div>

            <div className="bg-surface-sunken border-l-4 border-l-gray-400 p-6 my-6">
              <p className="text-ink-muted font-medium mb-2">
                {t('partner.situation.dilemma')}
              </p>
              <p className="text-ink italic text-lg">
                {t('partner.situation.dilemmaText')}
              </p>
            </div>

            <p className="text-ink font-semibold text-lg">
              {t('partner.situation.solution')}
            </p>
          </div>
        </section>

        {/* Grundidee */}
        <section className="py-20 md:py-28 bg-blue-500/10">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-6">
              {t('partner.coreIdea.title')}
            </h2>
            <div className="bg-surface p-8 rounded-lg shadow-sm border-2 border-blue-400/25">
              <p className="text-xl md:text-2xl text-ink font-medium mb-4">
                {t('partner.coreIdea.text')}
              </p>
              <p className="text-ink-muted italic">
                {t('partner.coreIdea.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Wer ist BROVKO */}
        <section className="py-20 md:py-28 bg-surface">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-8">
              {t('partner.whoBrovko.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-6">
                  <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-4 flex items-center gap-2">
                    <CheckCircle className="h-6 w-6 text-green-400" />
                    {t('partner.whoBrovko.what.title')}
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-ink-muted">{t('partner.whoBrovko.what.point1')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-ink-muted">{t('partner.whoBrovko.what.point2')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                      <span className="text-ink-muted">{t('partner.whoBrovko.what.point3')}</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-red-500">
                <CardContent className="p-6">
                  <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-4 flex items-center gap-2">
                    <XCircle className="h-6 w-6 text-red-400" />
                    {t('partner.whoBrovko.whatNot.title')}
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 font-bold flex-shrink-0">✕</span>
                      <span className="text-ink-muted">{t('partner.whoBrovko.whatNot.point1')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 font-bold flex-shrink-0">✕</span>
                      <span className="text-ink-muted">{t('partner.whoBrovko.whatNot.point2')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 font-bold flex-shrink-0">✕</span>
                      <span className="text-ink-muted">{t('partner.whoBrovko.whatNot.point3')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400 font-bold flex-shrink-0">✕</span>
                      <span className="text-ink-muted">{t('partner.whoBrovko.whatNot.point4')}</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
            <p className="text-ink-muted italic text-center">
              {t('partner.whoBrovko.note')}
            </p>
          </div>
        </section>

        {/* Zentrale Frage */}
        <section className="py-20 md:py-28 bg-surface-raised border-y-4 border-red-400/25">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="max-w-[64ch] mb-8">
              <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-4">
                {t('partner.centralQuestion.title')}
              </h2>
              <div className="bg-surface p-6 rounded-lg shadow-md inline-block">
                <p className="text-2xl text-ink font-bold mb-4">
                  {t('partner.centralQuestion.question')}
                </p>
                <p className="text-3xl text-red-400 font-bold">
                  {t('partner.centralQuestion.answer')}
                </p>
              </div>
            </div>

            <div className="bg-surface p-8 rounded-lg shadow-sm">
              <p className="text-ink-muted font-medium mb-4">
                {t('partner.centralQuestion.principle')}
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {[1, 2, 3, 4].map((num) => (
                  <div key={num} className="flex items-start gap-3 p-3 bg-red-500/10 rounded">
                    <ShieldCheck className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-ink font-medium">
                      {t(`partner.centralQuestion.rule${num}`)}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-ink-muted mb-3">
                {t('partner.centralQuestion.onRequest')}
              </p>
              <p className="text-ink-muted mb-3">
                {t('partner.centralQuestion.separation')}
              </p>
              <p className="text-ink font-bold text-lg">
                {t('partner.centralQuestion.nonnegotiable')}
              </p>
            </div>
          </div>
        </section>

        {/* Typischer Ablauf */}
        <section className="py-20 md:py-28 bg-surface">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-4">
              {t('partner.process.title')}
            </h2>
            <p className="text-ink-muted mb-8 italic">
              {t('partner.process.subtitle')}
            </p>

            <div className="space-y-6">
              {/* Step 1 */}
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-400 font-bold">1</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-ink mb-2">
                        {t('partner.process.step1.title')}
                      </h3>
                      <p className="text-ink-muted mb-1">{t('partner.process.step1.example1')}</p>
                      <p className="text-ink-muted">{t('partner.process.step1.example2')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-center">
                <ArrowRight className="h-6 w-6 text-ink-faint" />
              </div>

              {/* Step 2 */}
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-green-500/15 flex items-center justify-center flex-shrink-0">
                      <span className="text-green-400 font-bold">2</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-ink mb-2">
                        {t('partner.process.step2.title')}
                      </h3>
                      <div className="flex gap-4">
                        <span className="text-ink-muted text-sm">• {t('partner.process.step2.note1')}</span>
                        <span className="text-ink-muted text-sm">• {t('partner.process.step2.note2')}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-center">
                <ArrowRight className="h-6 w-6 text-ink-faint" />
              </div>

              {/* Step 3 */}
              <Card className="border-l-4 border-l-purple-500">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-purple-500/15 flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-400 font-bold">3</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-ink mb-2">
                        {t('partner.process.step3.title')}
                      </h3>
                      <ul className="space-y-1">
                        <li className="text-ink-muted">• {t('partner.process.step3.point1')}</li>
                        <li className="text-ink-muted">• {t('partner.process.step3.point2')}</li>
                        <li className="text-ink-muted">• {t('partner.process.step3.point3')}</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-center">
                <ArrowRight className="h-6 w-6 text-ink-faint" />
              </div>

              {/* Step 4 */}
              <Card className="border-l-4 border-l-orange-500">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-orange-500/15 flex items-center justify-center flex-shrink-0">
                      <span className="text-orange-400 font-bold">4</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-ink mb-2">
                        {t('partner.process.step4.title')}
                      </h3>
                      <p className="text-ink-muted mb-1">{t('partner.process.step4.point1')}</p>
                      <p className="text-ink font-medium">{t('partner.process.step4.point2')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Kooperationsmodelle */}
        <section className="py-20 md:py-28 bg-surface-sunken">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-4">
              {t('partner.models.title')}
            </h2>
            <p className="text-ink-muted mb-8">
              {t('partner.models.intro')}
            </p>

            <div className="space-y-6 mb-6">
              {/* Model 1 */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Target className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-ink mb-3">
                        {t('partner.models.model1.title')}
                      </h3>
                      <ul className="space-y-2 mb-3">
                        <li className="text-ink-muted">• {t('partner.models.model1.point1')}</li>
                        <li className="text-ink-muted">• {t('partner.models.model1.point2')}</li>
                      </ul>
                      <p className="text-blue-400 font-medium italic">
                        {t('partner.models.model1.ideal')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Model 2 */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <HandshakeIcon className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-ink mb-3">
                        {t('partner.models.model2.title')}
                      </h3>
                      <ul className="space-y-2 mb-3">
                        <li className="text-ink-muted">• {t('partner.models.model2.point1')}</li>
                        <li className="text-ink-muted">• {t('partner.models.model2.point2')}</li>
                      </ul>
                      <p className="text-green-400 font-medium italic">
                        {t('partner.models.model2.ideal')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Model 3 */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Users className="h-6 w-6 text-purple-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-ink mb-3">
                        {t('partner.models.model3.title')}
                      </h3>
                      <ul className="space-y-2 mb-3">
                        <li className="text-ink-muted">• {t('partner.models.model3.point1')}</li>
                        <li className="text-ink-muted">• {t('partner.models.model3.point2')}</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <p className="text-ink-muted italic text-center">
              {t('partner.models.note')}
            </p>
          </div>
        </section>

        {/* Umsatz & Fairness */}
        <section className="py-20 md:py-28 bg-surface">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-4">
              {t('partner.revenue.title')}
            </h2>
            <p className="text-ink-muted font-medium mb-6">
              {t('partner.revenue.intro')}
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {[1, 2, 3, 4].map((num) => (
                <div key={num} className="flex items-start gap-3 p-4 bg-green-500/10 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-ink-muted">
                    {t(`partner.revenue.point${num}`)}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-ink font-semibold text-lg">
              {t('partner.revenue.goal')}
            </p>
          </div>
        </section>

        {/* Was Partner gewinnen */}
        <section className="py-20 md:py-28 bg-blue-500/10">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-8">
              {t('partner.benefits.title')}
            </h2>
            <div className="space-y-4 mb-8">
              {[1, 2, 3, 4, 5].map((num) => (
                <div key={num} className="flex items-start gap-4 p-4 bg-surface rounded-lg shadow-sm">
                  <CheckCircle className="h-6 w-6 text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-ink-muted text-lg">
                    {t(`partner.benefits.point${num}`)}
                  </p>
                </div>
              ))}
            </div>
            <div className="bg-surface p-6 rounded-lg shadow-md">
              <p className="text-ink-muted mb-2">
                {t('partner.benefits.quoteNote')}
              </p>
              <p className="text-ink italic text-xl font-medium">
                {t('partner.benefits.quote')}
              </p>
            </div>
          </div>
        </section>

        {/* Für wen geeignet */}
        <section className="py-20 md:py-28 bg-surface">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-8">
              {t('partner.suitability.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-2 border-green-400/25">
                <CardContent className="p-6">
                  <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-green-300 mb-4 flex items-center gap-2">
                    <CheckCircle className="h-6 w-6" />
                    {t('partner.suitability.suitable.title')}
                  </h3>
                  <ul className="space-y-3">
                    {[1, 2, 3].map((num) => (
                      <li key={num} className="flex items-start gap-2">
                        <span className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                        <span className="text-ink-muted">{t(`partner.suitability.suitable.point${num}`)}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-red-400/25">
                <CardContent className="p-6">
                  <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-red-300 mb-4 flex items-center gap-2">
                    <XCircle className="h-6 w-6" />
                    {t('partner.suitability.notSuitable.title')}
                  </h3>
                  <ul className="space-y-3">
                    {[1, 2, 3].map((num) => (
                      <li key={num} className="flex items-start gap-2">
                        <span className="text-red-400 font-bold flex-shrink-0">✕</span>
                        <span className="text-ink-muted">{t(`partner.suitability.notSuitable.point${num}`)}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Kontaktformular */}
        <section id="partner-contact" className="py-20 md:py-28 bg-surface-sunken">
          <div className="container mx-auto px-4 max-w-2xl">
            <div className="max-w-[64ch] mb-8">
              <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-4">
                {t('partner.form.title')}
              </h2>
              <p className="text-ink-muted mb-4">
                {t('partner.form.subtitle')}
              </p>
              <p className="text-ink-muted italic">
                {t('partner.form.note')}
              </p>
            </div>

            {isSubmitted ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-ink mb-2">
                    {t('partner.form.success.title')}
                  </h3>
                  <p className="text-ink-muted">
                    {t('partner.form.success.message')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        {t('partner.form.name')} *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.name ? 'border-red-400' : 'border-hairline-strong'
                        }`}
                        placeholder={t('partner.form.name')}
                      />
                      {errors.name && (
                        <p className="text-red-400 text-sm mt-1">{errors.name}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        {t('partner.form.company')} *
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.company ? 'border-red-400' : 'border-hairline-strong'
                        }`}
                        placeholder={t('partner.form.company')}
                      />
                      {errors.company && (
                        <p className="text-red-400 text-sm mt-1">{errors.company}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        {t('partner.form.email')} *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.email ? 'border-red-400' : 'border-hairline-strong'
                        }`}
                        placeholder={t('partner.form.email')}
                      />
                      {errors.email && (
                        <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        {t('partner.form.message')}
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows="4"
                        className="w-full px-4 py-3 border border-hairline-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={t('partner.form.messagePlaceholder')}
                      />
                    </div>

                    {errors.submit && (
                      <div className="text-red-400 text-sm text-center">
                        {errors.submit}
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg font-semibold disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          {t('partner.form.submitting')}
                        </>
                      ) : (
                        t('partner.form.submit')
                      )}
                    </Button>

                    <p className="text-xs text-ink-muted text-center">
                      {t('partner.form.privacy')}
                    </p>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default PartnerProgramm;
